import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EnvelopeSimple, LockSimple, User, Eye, EyeSlash, CircleNotch, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export const apiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return "Algo salió mal. Intenta de nuevo.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.some((x) => x?.loc?.includes("email")) ? "Escribe un correo válido" : "Revisa los datos ingresados";
  return String(d);
};

const Field = ({ icon: Icon, type = "text", testid, right, ...props }) => (
  <label className="relative block">
    <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
    <input type={type} data-testid={testid} required {...props}
      className="w-full rounded-full border border-black/15 bg-white pl-11 pr-11 py-3 text-sm outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-4 focus:ring-primary/15" />
    {right}
  </label>
);

const PasswordField = ({ testid, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <Field icon={LockSimple} type={show ? "text" : "password"} testid={testid} {...props}
      right={
        <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testid}-toggle`} aria-label="Mostrar contraseña"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
          {show ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      } />
  );
};

const Submit = ({ busy, children, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid}
    className="w-full flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--brand-orange))] text-white font-bold py-3.5 hover:brightness-105 hover:-translate-y-0.5 active:translate-y-0 transition-[transform,filter] disabled:opacity-60">
    {busy && <CircleNotch size={18} className="animate-spin" />} {children}
  </button>
);

const ErrorMsg = ({ msg }) => msg ? <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{msg}</p> : null;

export const LoginForm = ({ onForgot }) => {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data); toast.success(`¡Hola, ${data.name}!`);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="login-form">
      <Field icon={EnvelopeSimple} type="email" placeholder="Correo electrónico" testid="login-email-input" autoComplete="email"
        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <PasswordField testid="login-password-input" placeholder="Contraseña" autoComplete="current-password"
        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      <div className="text-right">
        <button type="button" onClick={onForgot} data-testid="forgot-password-link" className="text-xs font-semibold text-primary hover:underline">¿Olvidaste tu contraseña?</button>
      </div>
      <ErrorMsg msg={err} />
      <Submit busy={busy} testid="login-submit-button">Ingresar</Submit>
    </form>
  );
};

export const RegisterForm = () => {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pwOk = form.password.length >= 8 && /[a-zA-Z]/.test(form.password) && /\d/.test(form.password);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!pwOk) { setErr("La contraseña debe tener al menos 8 caracteres, con letras y números"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data); toast.success("¡Cuenta creada! Bienvenido a Todito Ventas");
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="register-form">
      <Field icon={User} placeholder="Nombre completo" testid="register-name-input" autoComplete="name"
        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <Field icon={EnvelopeSimple} type="email" placeholder="Correo electrónico" testid="register-email-input" autoComplete="email"
        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <PasswordField testid="register-password-input" placeholder="Contraseña" autoComplete="new-password"
        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      <p className={`text-xs pl-4 flex items-center gap-1.5 ${pwOk ? "text-[hsl(var(--meli-green))]" : "text-muted-foreground"}`} data-testid="password-hint">
        <CheckCircle weight={pwOk ? "fill" : "regular"} size={14} /> Mínimo 8 caracteres, con letras y números
      </p>
      <ErrorMsg msg={err} />
      <Submit busy={busy} testid="register-submit-button">Crear cuenta</Submit>
    </form>
  );
};

export const ForgotForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/forgot-password", { email }); setSent(data.message); }
    catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="forgot-form">
      <button onClick={onBack} data-testid="forgot-back-button" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} weight="bold" /> Volver a ingresar
      </button>
      <h3 className="text-xl font-extrabold">Recupera tu contraseña</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-5">Te enviaremos un enlace a tu correo para crear una nueva.</p>
      {sent ? (
        <p data-testid="forgot-success" className="text-sm bg-green-50 border border-green-200 text-green-800 rounded-xl px-3 py-3">{sent}</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Field icon={EnvelopeSimple} type="email" placeholder="Correo electrónico" testid="forgot-email-input"
            value={email} onChange={(e) => setEmail(e.target.value)} />
          <ErrorMsg msg={err} />
          <Submit busy={busy} testid="forgot-submit-button">Enviar enlace</Submit>
        </form>
      )}
    </div>
  );
};

export const ResetForm = ({ token, onDone }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      toast.success(data.message); onDone();
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="reset-form">
      <h3 className="text-xl font-extrabold">Crea tu nueva contraseña</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-5">Mínimo 8 caracteres, con letras y números.</p>
      <form onSubmit={submit} className="space-y-3">
        <PasswordField testid="reset-password-input" placeholder="Nueva contraseña" autoComplete="new-password"
          value={pw} onChange={(e) => setPw(e.target.value)} />
        <ErrorMsg msg={err} />
        <Submit busy={busy} testid="reset-submit-button">Guardar contraseña</Submit>
      </form>
    </div>
  );
};
